/* eslint-disable @nx/enforce-module-boundaries */
import { FC, useState, useRef, useEffect } from 'react';
import { CardDetailsProps } from './CardDetails.types';
import { StyledCardDetails } from './CardDetails.styles';
import { Heading, Types, Button, Icon, CheckBox, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { StickyCardSection } from '../StickyCard/StickyCardSection';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import { CardDetailsRight } from './CardDetailsRight/CardDetailsRight';
import { isMobile, referAFriend, replaceWhitespaceAndAddBtn } from '../../modules/utils';
import { useSession } from '@marriott/mi-headless-utils';
import { useStore } from '../../modules/store/cobrandStore';

export const CardDetailsConfig = {
  emptyLabel: 'Card Details',
  isEmpty: () => true,
  resourceType: `/components/content/carddetails`,
};

export const CardDetailsComp: FC<CardDetailsProps> = (props: CardDetailsProps) => {
  const {
    header,
    cardSummary,
    cardType,
    expandAccordionForMobile,
    styleclass,
    componentId,
    linkUrl,
    openInaNewTab,
    linkText,
    accordionHeader = 'Overview',
    cardCode,
    trackingProperties,
    cardHeader,
    imagePath,
    imagePathWithoutBadge,
    isComparisonTrayEnabled,
  } = props;
  const linkType = openInaNewTab === 'true' ? 'external' : 'internal';
  const [showMobileAccordion, setshowMobileAccordion] = useState(true);
  const { datalayer } = useSession();
  const cardDetailsRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const toggleAccordion = () => {
    setshowMobileAccordion(!showMobileAccordion);
  };

  useEffect(() => {
    setshowMobileAccordion(!expandAccordionForMobile);
    if (datalayer) referAFriend(datalayer, cardDetailsRef);
  }, [datalayer]);

  const isMobileDevice: boolean = isMobile();
  const isViewportL = useCheckBreakpoint('viewportL');
  const { cobrandData, setCobrandData } = useStore(store => store);
  const [comparisonCheckBox, setComparisonCheckBox] = useState(false);

  useEffect(() => {
    const isChecked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
    setComparisonCheckBox(isChecked);
  }, [cobrandData.selectedCards, cardCode]);

  const setDisableState = () => {
    const isChecked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
    return isViewportL && !isMobileDevice
      ? cobrandData.selectedCards.length === 3 && !isChecked
      : cobrandData.selectedCards.length === 2 && !isChecked;
  };
  const handleCheckboxChange = () => {
    setComparisonCheckBox(prev => {
      const newCheckState = !prev;
      const updatedCards = newCheckState
        ? [
            ...cobrandData.selectedCards,
            {
              cardname: cardCode,
              checked: newCheckState,
              cardType: cardType,
              imagePath: imagePathWithoutBadge ? imagePathWithoutBadge : imagePath,
              header: cardHeader,
            },
          ]
        : cobrandData.selectedCards.filter(({ cardname }) => cardname !== cardCode);

      setCobrandData({ ...cobrandData, selectedCards: updatedCards });
      return newCheckState;
    });
  };

  return (
    <StyledCardDetails
      data-component-name="o-cobrand-carddetails"
      data-testid="carddetails"
      className={clsx('card-details', 'mt-2 mt-md-5 pt-3 mb-2 mb-md-5 pb-3', styleclass)}
      data-cardtype={cardType}
      id={componentId}
      ref={cardDetailsRef}
    >
      <div className="pt-md-0 card-detail--border t-background-color">
        <div className="card-detail-wrapper pt-lg-4 mt-5 px-lg-4 px-3 pb-5">
          <div className="mr-lg-5">
            <Heading
              titleText={header}
              variation={Types.headingType.title}
              fontSize={Types.size.medium}
              customClass={clsx(
                'text-md-left mb-4 mb-lg-5 pb-lg-2 ml-md-3 pl-xl-4 pl-lg-5 col-md-10 col-lg-12 d-none d-md-block'
              )}
            />
          </div>
          <div className="d-flex flex-column flex-md-row">
            <div className="col-xl-3 col-lg-4 col-md-4 mx-auto col-12 d-flex justify-content-center">
              <div className="d-flex flex-column">
                <StickyCardSection
                  {...props}
                  variant="vertical"
                  hideButtonOnMobile={true}
                  cardTitle={cardSummary}
                  header={header}
                />
                <div className="accordion m-accordion-container d-block d-md-none px-0">
                  <Button
                    className={clsx([`accordion-button py-3`])}
                    ariaExpanded={showMobileAccordion ? false : true}
                    callback={toggleAccordion}
                  >
                    <div className="m-accordion-title">
                      {accordionHeader}
                      <Icon
                        iconClass={clsx(
                          'accordion-arrow-icon',
                          showMobileAccordion ? 'icon-arrow-up' : 'icon-arrow-down'
                        )}
                      />
                    </div>
                  </Button>
                  <div className={clsx('accordion-collapse', showMobileAccordion ? 'collapse d-none' : '')}>
                    <CardDetailsRight {...props} showAccordion={true} />
                  </div>
                </div>
                <div className="d-flex justify-content-center flex-column align-items-center">
                  <Button
                    id={replaceWhitespaceAndAddBtn(componentId)}
                    isLink={true}
                    href={linkUrl}
                    trackingProperties={trackingProperties}
                    target={openInaNewTab === 'true' ? '_blank' : '_self'}
                    className={clsx('m-button-m', 'py-2 mb-1 mr-0')}
                    linkType={linkType}
                    ref={ref}
                    buttonCopy={linkText}
                    linkAriaLabelOpenNewWindow={openInaNewTab === 'true' ? 'Open in new tab' : ''}
                  />

                  {isComparisonTrayEnabled && (
                    <div className={' mt-4 '}>
                      <CheckBox
                        checkboxLabel={`Compare`}
                        aria-label={'compare-' + replaceWhitespaceAndAddBtn(componentId) + '-checkbox'}
                        onChange={handleCheckboxChange}
                        checked={comparisonCheckBox}
                        checkboxName={replaceWhitespaceAndAddBtn(componentId) + '-checkbox'}
                        checkboxId={replaceWhitespaceAndAddBtn(componentId) + '-checkbox'}
                        disabled={setDisableState()}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <CardDetailsRight {...props} componentId={componentId + '-content'} showAccordion={false} />
          </div>
        </div>
      </div>
    </StyledCardDetails>
  );
};

export const CardDetails = (props: CardDetailsProps) => {
  return (
    <EditableComponent config={CardDetailsConfig} {...props}>
      <CardDetailsComp {...props} />
    </EditableComponent>
  );
};
